import { useEffect, useState } from "react"
import { ApolloQueryResult, FetchMoreOptions, FetchMoreQueryOptions, NetworkStatus } from "@apollo/client"
import { PageInfo } from "@/gql/graphql"

interface UseHandleListEndReachedOptions {
  pageInfo?: Pick<PageInfo, "hasNextPage" | "endCursor">
  first?: number
  observerRef: React.RefObject<null>
  networkStatus: NetworkStatus
  fetchMore?: (
    fetchMoreOptions: FetchMoreQueryOptions<any, any> & FetchMoreOptions<any, any>
  ) => Promise<ApolloQueryResult<any>>
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>
}

export const useHandleListEndReached = ({
  pageInfo,
  first,
  networkStatus,
  fetchMore,
  observerRef,
  scrollContainerRef,
}: UseHandleListEndReachedOptions) => {
  const [isFetching, setIsFetching] = useState(false)

  useEffect(() => {
    let intersectionObserver: IntersectionObserver | null = null

    const endCursor = pageInfo?.endCursor
    const hasNextPage = pageInfo?.hasNextPage

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      const firstEntry = entries[0]
      if (firstEntry.isIntersecting && hasNextPage && networkStatus !== NetworkStatus.fetchMore && !isFetching) {
        setIsFetching(true)
        fetchMore?.({
          variables: {
            first,
            after: endCursor,
          },
        }).finally(() => {
          setIsFetching(false)
        })
      }
    }

    const setupIntersectionObserver = (element: Element) => {
      intersectionObserver?.disconnect()

      intersectionObserver = new IntersectionObserver(handleIntersection, {
        threshold: 0.1,
        root: scrollContainerRef?.current || null,
        rootMargin: "0px",
      })

      intersectionObserver.observe(element)
    }

    // Setup mutation observer to watch for ref changes
    const mutationObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === "childList" && observerRef.current) {
          setupIntersectionObserver(observerRef.current)
        }
      }
    })

    // Initial setup if ref is already available
    if (observerRef.current) {
      setupIntersectionObserver(observerRef.current)
    }

    // Start observing document body for changes
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => {
      intersectionObserver?.disconnect()
      mutationObserver.disconnect()
    }
  }, [
    fetchMore,
    first,
    isFetching,
    networkStatus,
    observerRef,
    pageInfo?.endCursor,
    pageInfo?.hasNextPage,
    scrollContainerRef,
  ])
}
